import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Power, Eye, AlertTriangle, ShieldCheck, DoorOpen, DoorClosed } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * AdminMaintenancePanel
 * -----------------------------------------------------------------------------
 * Site-wide access controls, grouped together so an admin can review and flip
 * them in one place:
 *   - Gateway Login Portal (ON/OFF)   — Temble-compliance login gate
 *   - Maintenance Mode (ON/OFF)       — full-site lockdown + copy editor
 *
 * Admin bypass: anyone with a valid admin token in localStorage keeps normal
 * access to the storefront, so the admin can flip either switch back off.
 */
const GatewayPortalCard = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [enabled, setEnabled] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/gateway", { headers });
            setEnabled(!!data.enabled);
        } catch {
            toast.error("Failed to load gateway portal state");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const toggle = async () => {
        if (enabled === null) return;
        const next = !enabled;
        setSaving(true);
        setEnabled(next); // optimistic
        try {
            const { data } = await api.patch(
                "/admin/gateway",
                { gateway_enabled: next },
                { headers },
            );
            setEnabled(!!data.enabled);
            toast.success(
                data.enabled
                    ? "Gateway Login Portal ENABLED — visitors must sign in first."
                    : "Gateway Login Portal DISABLED — storefront is directly browsable.",
            );
        } catch (err) {
            setEnabled(!next); // revert
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    if (enabled === null) {
        return (
            <div className="glass-elevated rounded-2xl p-5 text-[var(--text-secondary)]" data-testid="gateway-card-loading">
                Loading…
            </div>
        );
    }

    return (
        <div
            className={`glass-elevated rounded-2xl p-5 border ${
                enabled
                    ? "border-[var(--neon-cyan,#2abbc9)]/40 bg-[rgba(42,187,201,0.05)]"
                    : "border-amber-500/40 bg-amber-500/5"
            }`}
            data-testid="gateway-portal-card"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    {enabled ? (
                        <DoorClosed size={22} className="text-[var(--neon-cyan,#2abbc9)] mt-0.5 shrink-0" />
                    ) : (
                        <DoorOpen size={22} className="text-amber-300 mt-0.5 shrink-0" />
                    )}
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                            Gateway Login Portal
                        </div>
                        <div
                            className={`text-lg font-bold ${
                                enabled ? "text-[var(--neon-cyan,#2abbc9)]" : "text-amber-200"
                            }`}
                            data-testid="gateway-portal-status-label"
                        >
                            {enabled ? "ON — Gateway Enabled" : "OFF — Gateway Disabled"}
                        </div>
                        <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-md">
                            {enabled
                                ? "Unauthenticated visitors see the standalone Secure Researcher Access gateway before any storefront content."
                                : "Storefront is directly browsable. The gateway page is preserved and can be switched back on at any time."}
                        </p>
                    </div>
                </div>

                <button
                    onClick={toggle}
                    disabled={saving}
                    aria-pressed={enabled}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                        enabled
                            ? "border-amber-500/60 text-amber-200 hover:bg-amber-500/10"
                            : "border-[var(--neon-cyan,#2abbc9)]/60 text-[var(--neon-cyan,#2abbc9)] hover:bg-[rgba(42,187,201,0.08)]"
                    }`}
                    data-testid="gateway-portal-toggle"
                >
                    <Power size={14} />
                    {saving ? "Saving…" : enabled ? "Disable gateway" : "Enable gateway"}
                </button>
            </div>
        </div>
    );
};

const AfterpayBadgeToggle = ({ headers }) => {
    const [on, setOn] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/site/display").then(({ data }) => setOn(!!data.show_afterpay_badge))
            .catch(() => toast.error("Failed to load badge setting"));
    }, []);
    const flip = async () => {
        const next = !on;
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/display", { show_afterpay_badge: next }, { headers });
            setOn(!!data.show_afterpay_badge);
            toast.success(`Afterpay badge ${next ? "shown" : "hidden"} on products`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };
    return (
        <div className="flex items-center justify-between gap-4 pt-4 mt-1 border-t border-white/10" data-testid="afterpay-badge-row">
            <div>
                <div className="text-sm font-semibold text-white">Show "Afterpay Available" badge</div>
                <div className="text-[11px] text-[var(--text-muted)]">
                    Visual only · product cards, product page and wishlist. Does not change checkout.
                </div>
            </div>
            <button
                onClick={flip}
                disabled={on === null || saving}
                aria-pressed={!!on}
                data-testid="afterpay-badge-toggle"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-all ${
                    on
                        ? "border-[var(--neon-cyan,#2abbc9)]/60 text-[var(--neon-cyan,#2abbc9)] bg-[rgba(42,187,201,0.08)]"
                        : "border-amber-500/50 text-amber-200 bg-amber-500/5"
                }`}
            >
                <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-[var(--neon-cyan,#2abbc9)]" : "bg-amber-400"}`} />
                {on === null ? "Loading…" : saving ? "Saving…" : on ? "Shown" : "Hidden"}
            </button>
        </div>
    );
};

const PaymentRailsCard = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [rails, setRails] = useState(null);
    const [saving, setSaving] = useState(null);

    const RAIL_LABELS = [
        { key: "stripe_card", label: "Stripe · Card", sub: "Visa, Mastercard, Amex via Stripe" },
        { key: "apple_pay", label: "Apple Pay", sub: "iOS wallet (routed via Stripe)" },
        { key: "google_pay", label: "Google Pay", sub: "Android wallet (routed via Stripe)" },
        { key: "afterpay", label: "Afterpay", sub: "BNPL via Stripe" },
        { key: "zip_pay", label: "Zip Pay", sub: "BNPL via Stripe" },
        { key: "bank_transfer", label: "Bank Transfer", sub: "Direct deposit · manual verification" },
        { key: "payid", label: "PayID", sub: "Instant transfer · manual verification" },
    ];

    const load = async () => {
        try {
            const { data } = await api.get("/admin/payment-rails", { headers });
            setRails(data);
        } catch {
            toast.error("Failed to load payment rails");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const flip = async (key) => {
        if (!rails) return;
        const next = !rails[key];
        setSaving(key);
        setRails({ ...rails, [key]: next });
        try {
            const { data } = await api.patch(
                "/admin/payment-rails",
                { [key]: next },
                { headers },
            );
            setRails(data);
            toast.success(`${key} ${next ? "enabled" : "disabled"}`);
        } catch (err) {
            setRails({ ...rails, [key]: !next });
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setSaving(null);
    };

    if (!rails) {
        return (
            <div className="glass-elevated rounded-2xl p-5 text-[var(--text-secondary)]" data-testid="payment-rails-loading">
                Loading payment rails…
            </div>
        );
    }

    return (
        <div className="glass-elevated rounded-2xl p-5 border border-white/10" data-testid="payment-rails-card">
            <div className="flex items-start gap-3 mb-4">
                <ShieldCheck size={20} className="text-[var(--neon-cyan,#2abbc9)] mt-0.5 shrink-0" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                        Payment Rails · Independent toggles
                    </div>
                    <div className="text-lg font-bold text-white">Payment Methods Available at Checkout</div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-md">
                        Flip each rail on or off independently. Changes apply to new checkouts
                        immediately. Flipping Bank Transfer or PayID also updates the public
                        /payment-methods/public response.
                    </p>
                </div>
            </div>

            <div className="divide-y divide-white/5">
                {RAIL_LABELS.map(({ key, label, sub }) => {
                    const on = !!rails[key];
                    return (
                        <div key={key} className="flex items-center justify-between gap-4 py-3" data-testid={`rail-row-${key}`}>
                            <div>
                                <div className="text-sm font-semibold text-white">{label}</div>
                                <div className="text-[11px] text-[var(--text-muted)]">{sub}</div>
                            </div>
                            <button
                                onClick={() => flip(key)}
                                disabled={saving === key}
                                aria-pressed={on}
                                data-testid={`rail-toggle-${key}`}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-all ${
                                    on
                                        ? "border-[var(--neon-cyan,#2abbc9)]/60 text-[var(--neon-cyan,#2abbc9)] bg-[rgba(42,187,201,0.08)]"
                                        : "border-amber-500/50 text-amber-200 bg-amber-500/5"
                                }`}
                            >
                                <span className={`inline-block w-2 h-2 rounded-full ${on ? "bg-[var(--neon-cyan,#2abbc9)]" : "bg-amber-400"}`} />
                                {saving === key ? "Saving…" : on ? "Enabled" : "Disabled"}
                            </button>
                        </div>
                    );
                })}
            </div>
            <AfterpayBadgeToggle headers={headers} />
        </div>
    );
};


export const AdminMaintenancePanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [state, setState] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/maintenance", { headers });
            setState(data);
        } catch {
            toast.error("Failed to load maintenance settings");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const update = (k, v) => setState((p) => ({ ...p, [k]: v }));

    const save = async (overrides) => {
        setSaving(true);
        try {
            const payload = {
                maintenance_mode_enabled: state.enabled,
                maintenance_primary_message: state.primary_message,
                maintenance_secondary_message: state.secondary_message,
                ...(overrides || {}),
            };
            const { data } = await api.patch("/admin/maintenance", payload, { headers });
            setState(data);
            toast.success(
                payload.maintenance_mode_enabled
                    ? "Maintenance mode ENABLED — storefront is locked."
                    : "Maintenance mode disabled — storefront is live."
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    const toggleEnabled = async () => {
        const next = !state.enabled;
        // Optimistic UI + explicit save so the switch feels immediate.
        setState((p) => ({ ...p, enabled: next }));
        await save({ maintenance_mode_enabled: next });
    };

    const preview = () => {
        window.open("/?preview=maintenance", "_blank", "noopener,noreferrer");
    };

    if (!state) {
        return (
            <div className="text-[var(--text-secondary)] py-12 text-center">
                Loading…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="admin-maintenance-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-bold text-white inline-flex items-center gap-2">
                    <Power size={18} className={state.enabled ? "text-red-400" : "text-[var(--gold)]"} />
                    Website Settings · Access Controls
                </h3>
                <button
                    onClick={load}
                    className="btn-neon"
                    data-testid="maintenance-refresh"
                >
                    <RefreshCw size={14} /> Reload
                </button>
            </div>

            {/* Gateway Login Portal (independent toggle) */}
            <GatewayPortalCard token={token} />

            {/* Payment Rails — 7 independent toggles */}
            <PaymentRailsCard token={token} />

            {/* Maintenance status card (full-site lockdown) */}
            <div
                className={`glass-elevated rounded-2xl p-5 border ${
                    state.enabled
                        ? "border-red-500/40 bg-red-500/5"
                        : "border-emerald-500/30 bg-emerald-500/5"
                }`}
                data-testid="maintenance-status-card"
            >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3">
                        {state.enabled ? (
                            <AlertTriangle size={22} className="text-red-400 mt-0.5 shrink-0" />
                        ) : (
                            <ShieldCheck size={22} className="text-emerald-400 mt-0.5 shrink-0" />
                        )}
                        <div>
                            <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Storefront status
                            </div>
                            <div
                                className={`text-lg font-bold ${
                                    state.enabled ? "text-red-300" : "text-emerald-300"
                                }`}
                                data-testid="maintenance-status-label"
                            >
                                {state.enabled ? "LOCKED — public traffic blocked" : "LIVE — storefront is public"}
                            </div>
                            <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-md">
                                {state.enabled
                                    ? "Only admins with a valid token can access the site and APIs."
                                    : "Everyone can browse, buy, and check out normally."}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={toggleEnabled}
                        disabled={saving}
                        className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-all ${
                            state.enabled
                                ? "border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/10"
                                : "border-red-500/60 text-red-300 hover:bg-red-500/10"
                        }`}
                        data-testid="maintenance-toggle"
                    >
                        <Power size={14} />
                        {saving ? "Saving…" : state.enabled ? "Bring site back online" : "Lock the storefront"}
                    </button>
                </div>
            </div>

            {/* Copy editors */}
            <div className="glass rounded-2xl p-5 space-y-4">
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                        Primary headline
                    </label>
                    <input
                        type="text"
                        maxLength={240}
                        value={state.primary_message || ""}
                        onChange={(e) => update("primary_message", e.target.value)}
                        placeholder="We're upgrading the lab — back shortly."
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="maintenance-primary-input"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                        Supporting message
                    </label>
                    <input
                        type="text"
                        maxLength={240}
                        value={state.secondary_message || ""}
                        onChange={(e) => update("secondary_message", e.target.value)}
                        placeholder="Thank you for your patience."
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                        data-testid="maintenance-secondary-input"
                    />
                </div>

                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={() => save()}
                        disabled={saving}
                        className="btn-solid"
                        data-testid="maintenance-save"
                    >
                        <Save size={14} /> {saving ? "Saving…" : "Save messages"}
                    </button>
                    <button
                        onClick={preview}
                        className="btn-neon"
                        data-testid="maintenance-preview"
                    >
                        <Eye size={14} /> Preview page
                    </button>
                </div>
            </div>

            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] leading-relaxed">
                Admin bypass: keep this browser signed into the admin panel (token in local storage) and you can still
                browse the storefront while it&apos;s locked. If you sign out or the token is cleared, you&apos;ll be
                shown the maintenance page like everyone else.
            </div>
        </div>
    );
};

export default AdminMaintenancePanel;
